/**
 * "Koble til <provider>" — Zitadel user/v2 account-linking client.
 *
 * Implements Path A of infra/zitadel/CONNECT-GOOGLE-SPEC.md: three
 * authenticated calls made DIRECTLY from the SPA to Zitadel's own v2 API
 * (StartIdentityProviderIntent → RetrieveIdentityProviderIntent → AddIDPLink),
 * using the CALLER'S OWN OIDC access token — never a service credential. This
 * is what makes the flow self-service-safe: `AddIDPLink` only succeeds
 * without extra permission when `userId == token sub`
 * (`internal/command/user_idp_link.go`, CONNECT-GOOGLE-SPEC.md §7); linking
 * to anyone else's account 403s. A companion `ListIDPLinks` call (via
 * `isIdpLinked`) detects an existing link for the connected/disconnected UI
 * state.
 *
 * This module is PROVIDER-AGNOSTIC by construction: every call already took
 * an `idpId` parameter rather than hard-coding Google, so the same client +
 * decision table serve "Koble til Google" and "Koble til Facebook" (and any
 * future IdP) without duplication — see `./providers.ts` for the per-provider
 * config (idpId + display copy) that callers pass in.
 *
 * Security invariants (mirrors ../api.ts):
 *  - The access token goes ONLY in the Authorization header — never the DOM,
 *    a URL, or a log line.
 *  - Zitadel's short-lived idp-intent `id`/`token` pair (returned on the
 *    browser redirect back from the external IdP — see
 *    `parseIdpLinkCallbackParams`) is NOT our access token; it is read once
 *    by the callback page and used immediately, never persisted (the
 *    callback page scrubs it from the URL).
 *  - A 401/403 here means the caller's token lacks the reserved
 *    `urn:zitadel:iam:org:project:id:zitadel:aud` audience scope (an
 *    existing session predating that scope change) or has expired — either
 *    way the UI must prompt a normal re-login, never fail silently.
 *  - `AddIDPLink`'s target `sub` MUST always be the caller's OWN subject
 *    (never a value taken from an untrusted query param) — see
 *    `completeIdpLinkCallback`'s decision table below.
 */

import { config } from "../config";

const ZITADEL_BASE = config.oidc.issuer;

/**
 * A Zitadel v2 API call was rejected as unauthenticated/unauthorized (401/403).
 * Most commonly: the caller's access token predates the
 * `urn:zitadel:iam:org:project:id:zitadel:aud` scope change, or has expired.
 * The UI must surface a re-login prompt, not a generic error.
 */
export class IdpLinkUnauthorizedError extends Error {
  constructor() {
    super("Session expired, or missing the required scope — please log in again.");
    this.name = "IdpLinkUnauthorizedError";
  }
}

/**
 * `AddIDPLink` reported the external identity is already attached to a
 * DIFFERENT Zitadel user (gRPC/Connect `AlreadyExists`, mapped to HTTP 409).
 * The UI must show "already connected elsewhere" and must NOT retry/merge.
 */
export class IdpLinkTakenError extends Error {
  constructor() {
    super("This account is already linked to a different account.");
    this.name = "IdpLinkTakenError";
  }
}

/** Shared POST wrapper for the Zitadel v2 endpoints used by this flow. */
async function zitadelPost<T>(path: string, body: unknown, accessToken: string): Promise<T> {
  const response = await fetch(`${ZITADEL_BASE}${path}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  if (response.status === 401 || response.status === 403) {
    throw new IdpLinkUnauthorizedError();
  }
  if (response.status === 409) {
    throw new IdpLinkTakenError();
  }
  if (!response.ok) {
    throw new Error(`POST ${path} failed: ${response.status} ${response.statusText}`);
  }

  // AddIDPLink's success body is uninteresting to callers here; guard against
  // an empty body rather than assume every 2xx has JSON to parse.
  const text = await response.text();
  return (text ? (JSON.parse(text) as T) : ({} as T)) as T;
}

export interface StartIntentResult {
  authUrl: string;
}

/**
 * Step 1 (CONNECT-GOOGLE-SPEC.md §1) — start the intent. `successUrl` and
 * `failureUrl` are typically the SAME app.desjeff.net route: Zitadel tells
 * success from failure via query params (`id`&`token`&`user` vs
 * `error`&`error_description`), not via which URL it lands on.
 */
export function startIdpLinkIntent(
  idpId: string,
  successUrl: string,
  failureUrl: string,
  accessToken: string,
): Promise<StartIntentResult> {
  return zitadelPost<StartIntentResult>(
    "/v2/idp_intents",
    { idpId, urls: { successUrl, failureUrl } },
    accessToken,
  );
}

export interface RetrievedIntent {
  idpInformation?: {
    idpId?: string;
    userId?: string;
    userName?: string;
  };
  /** The Zitadel user this external identity is ALREADY linked to, if any. */
  userId?: string;
}

/** Step 3a — read back the completed external-IdP OAuth round-trip. */
export function retrieveIdpLinkIntent(
  intentId: string,
  intentToken: string,
  accessToken: string,
): Promise<RetrievedIntent> {
  return zitadelPost<RetrievedIntent>(
    `/v2/idp_intents/${encodeURIComponent(intentId)}`,
    { idpIntentToken: intentToken },
    accessToken,
  );
}

/**
 * Step 3b — attach the proven external identity to the CALLER'S OWN `sub`.
 * `sub` MUST be the logged-in user's own subject (never someone else's) —
 * that is what makes this a self-service call server-side.
 */
export function addIdpLink(
  sub: string,
  idpId: string,
  externalUserId: string,
  externalUserName: string | undefined,
  accessToken: string,
): Promise<unknown> {
  return zitadelPost(
    `/v2/users/${encodeURIComponent(sub)}/links`,
    {
      idpLink: {
        idpId,
        userId: externalUserId,
        userName: externalUserName,
      },
    },
    accessToken,
  );
}

export interface IdpLinkSummary {
  idpId: string;
  userId: string;
  userName?: string;
}

interface ListLinksResult {
  result?: IdpLinkSummary[];
}

/** Detect the "already linked" UI state via `ListIDPLinks` (self-readable). */
export async function isIdpLinked(
  sub: string,
  idpId: string,
  accessToken: string,
): Promise<boolean> {
  const data = await zitadelPost<ListLinksResult>(
    `/v2/users/${encodeURIComponent(sub)}/links/_search`,
    {},
    accessToken,
  );
  return (data.result ?? []).some((link) => link.idpId === idpId);
}

// ---------------------------------------------------------------------------
// Callback parsing + orchestration (used by IdpLinkCallbackPage)
// ---------------------------------------------------------------------------

export type IdpLinkCallbackParams =
  | { kind: "success"; id: string; token: string; user?: string }
  | { kind: "failure"; id?: string; error: string; errorDescription?: string };

/**
 * Parse the query string Zitadel appends to `successUrl`/`failureUrl`
 * (CONNECT-GOOGLE-SPEC.md §1/§3). Param names are literally `id`/`token`/
 * `user` on success, `id`/`error`/`error_description` on failure — identical
 * shape for every IdP, since Zitadel (not the external provider) generates
 * this redirect.
 */
export function parseIdpLinkCallbackParams(search: string): IdpLinkCallbackParams {
  const params = new URLSearchParams(search);
  const error = params.get("error");
  if (error) {
    return {
      kind: "failure",
      id: params.get("id") ?? undefined,
      error,
      errorDescription: params.get("error_description") ?? undefined,
    };
  }

  const id = params.get("id");
  const token = params.get("token");
  if (!id || !token) {
    return { kind: "failure", error: "missing_params" };
  }

  return { kind: "success", id, token, user: params.get("user") ?? undefined };
}

export type IdpLinkOutcome =
  | { status: "success" }
  | { status: "taken-elsewhere" }
  | { status: "cancelled"; message?: string }
  | { status: "relogin" }
  | { status: "error"; message: string };

/**
 * Run steps 3–4 (retrieve → decide → link) against an already-parsed
 * callback. Pure orchestration over the calls above — no DOM/history access
 * here (that stays in IdpLinkCallbackPage) so this is unit-testable with a
 * mocked fetch alone. Implements the decision table from
 * CONNECT-GOOGLE-SPEC.md §1/§6, identically for every provider:
 *  - `error` present            → cancelled/IdP failure.
 *  - retrieved `userId` absent  → not linked yet → AddIDPLink.
 *  - retrieved `userId == sub`  → already linked to THIS account → idempotent success.
 *  - retrieved `userId != sub`  → linked to a DIFFERENT account → taken-elsewhere.
 *  - AddIDPLink → 409           → taken-elsewhere (race with the check above).
 *  - any call → 401/403         → relogin.
 *
 * `sub` is always the CALLER's own subject (from the authenticated session),
 * never the untrusted `user` query param — that param is only ever compared
 * against `sub`, never used as the AddIDPLink target.
 */
export async function completeIdpLinkCallback(
  params: IdpLinkCallbackParams,
  sub: string,
  idpId: string,
  accessToken: string,
): Promise<IdpLinkOutcome> {
  if (params.kind === "failure") {
    if (params.error === "missing_params") {
      return { status: "error", message: "Mangler nødvendige parametre fra innloggingen." };
    }
    return { status: "cancelled", message: params.errorDescription };
  }

  try {
    const retrieved = await retrieveIdpLinkIntent(params.id, params.token, accessToken);
    const linkedUserId = retrieved.userId || params.user;
    const externalUserId = retrieved.idpInformation?.userId;

    if (linkedUserId && linkedUserId !== sub) {
      return { status: "taken-elsewhere" };
    }
    if (linkedUserId === sub) {
      // Already linked to THIS account (e.g. a retried/duplicate callback) —
      // idempotent success, no need to call AddIDPLink again.
      return { status: "success" };
    }
    if (!externalUserId) {
      return { status: "error", message: "Fikk ikke identiteten fra tilbyderen." };
    }

    await addIdpLink(sub, idpId, externalUserId, retrieved.idpInformation?.userName, accessToken);
    return { status: "success" };
  } catch (err) {
    if (err instanceof IdpLinkUnauthorizedError) return { status: "relogin" };
    if (err instanceof IdpLinkTakenError) return { status: "taken-elsewhere" };
    return {
      status: "error",
      message: err instanceof Error ? err.message : "Ukjent feil.",
    };
  }
}
